/**
 * Jack Daniels' VDOT calculations.
 * All paces returned as seconds-per-kilometer.
 * Race times in seconds; distances in metres.
 */

// VO2 for a given velocity (metres/min) — Daniels' regression
function vo2AtVelocity(vMpmin) {
  return -4.6 + 0.182258 * vMpmin + 0.000104 * vMpmin * vMpmin;
}

// %VO2max the body can sustain for t minutes
function pctVo2maxForDuration(tMin) {
  return (
    0.8 +
    0.1894393 * Math.exp(-0.012778 * tMin) +
    0.2989558 * Math.exp(-0.1932605 * tMin)
  );
}

// Solve vo2AtVelocity(v) = target by quadratic formula
function velocityForVo2(vo2) {
  // 0.000104 v^2 + 0.182258 v + (-4.6 - vo2) = 0
  const a = 0.000104;
  const b = 0.182258;
  const c = -4.6 - vo2;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return 0;
  return (-b + Math.sqrt(disc)) / (2 * a);
}

/**
 * VDOT from a race: distance_m + time_seconds → VDOT score.
 */
export function computeVdot(distanceMeters, timeSeconds) {
  if (!distanceMeters || !timeSeconds || timeSeconds <= 0) return null;
  const tMin = timeSeconds / 60;
  const vMpmin = distanceMeters / tMin;
  const vo2 = vo2AtVelocity(vMpmin);
  const pct = pctVo2maxForDuration(tMin);
  return vo2 / pct;
}

/**
 * Predict race time (seconds) for a distance at a given VDOT.
 * Iterative solve since %VO2max depends on duration.
 */
export function predictRaceSeconds(vdot, distanceMeters) {
  if (!vdot || !distanceMeters) return null;
  // Seed with 6 min/km
  let tMin = (distanceMeters / 1000) * 6;
  for (let i = 0; i < 20; i++) {
    const pct = pctVo2maxForDuration(tMin);
    const targetVo2 = vdot * pct;
    const v = velocityForVo2(targetVo2); // m/min
    if (v <= 0) return null;
    const next = distanceMeters / v;
    if (Math.abs(next - tMin) < 0.001) {
      tMin = next;
      break;
    }
    tMin = next;
  }
  return tMin * 60;
}

/**
 * Daniels training pace ranges by %VDOT.
 * Returns seconds-per-km per zone (pace = slower → faster).
 */
export function trainingPaces(vdot) {
  if (!vdot) return null;
  // Each zone: an effort bracket (low %, high %). The LOW end is the slow pace, HIGH end is the fast pace.
  const zones = {
    easy: [0.59, 0.74], // Easy/recovery — very slow to moderate
    marathon: [0.75, 0.84],
    threshold: [0.83, 0.88],
    interval: [0.95, 1.0],
    repetition: [1.05, 1.1],
  };
  const out = {};
  for (const [name, [lo, hi]] of Object.entries(zones)) {
    const vLo = velocityForVo2(vdot * lo); // slower velocity
    const vHi = velocityForVo2(vdot * hi); // faster velocity
    if (vLo <= 0 || vHi <= 0) continue;
    out[name] = {
      slow_sec_per_km: 60_000 / vLo, // 1000 m at vLo m/min = 1000/vLo min = 60000/vLo sec
      fast_sec_per_km: 60_000 / vHi,
    };
  }
  return out;
}

// ---- Formatting helpers ----

export function secToMinSec(sec) {
  if (!sec || sec <= 0 || !isFinite(sec)) return "—";
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function secToHMS(sec) {
  if (!sec || sec <= 0 || !isFinite(sec)) return "—";
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function paceRangeLabel(zone) {
  if (!zone) return "—";
  // Open-ended ranges (Z1 floor / Z5 ceiling) use the convention coaches
  // type them in: "6:27+" and "< 4:58" — clearer than a degenerate range.
  if (zone.bound === "lower") return `${secToMinSec(zone.slow_sec_per_km)}+/km`;
  if (zone.bound === "upper") return `< ${secToMinSec(zone.fast_sec_per_km)}/km`;
  return `${secToMinSec(zone.fast_sec_per_km)} – ${secToMinSec(zone.slow_sec_per_km)}/km`;
}

// Fixed race distances shown on profile
export const REFERENCE_RACES = [
  { key: "5k", label: "5K", meters: 5000 },
  { key: "10k", label: "10K", meters: 10000 },
  { key: "half", label: "Half Marathon", meters: 21097.5 },
  { key: "marathon", label: "Marathon", meters: 42195 },
];

/**
 * Map a workout type → training pace zone key for pace suggestions.
 */
export function workoutTypePaceKey(type) {
  switch (type) {
    case "easy":
    case "recovery":
    case "long":
    case "warmup":
    case "cooldown":
      return "easy";
    case "tempo":
      return "marathon";
    case "threshold":
      return "threshold";
    case "speed":
    case "intervals":
    case "fartlek":
      return "interval";
    case "race_pace":
    case "race":
      return "marathon";
    default:
      return null;
  }
}

// Map a single Z-number → pace bucket key. Internal helper for the
// multi-zone aggregator below.
function zoneNumberToPaceKey(n) {
  if (n >= 5) return "interval";
  if (n === 4) return "threshold";
  if (n === 3) return "marathon";
  if (n === 2) return "easy";
  if (n === 1) return "recovery";
  return null;
}

/**
 * Map a zones array (e.g. [3,4]) → training pace zone key.
 * Uses the HIGHEST zone selected (pace bracket tightens with intensity).
 *
 * Z1 (recovery) and Z2 (endurance/easy) get their own buckets so a coach
 * who has both Z1 and Z2 paces on a lactate test sees the right one. Older
 * VDOT-only profiles still resolve fine — they only fill the "easy" bucket
 * so Z1 falls back to it (see `pacesForType` / `lactateTrainingPaces`).
 *
 * NOTE: this picks ONE key. For multi-zone display (Z1+Z2, Z3+Z4) the
 * editor uses `aggregateZoneRanges` instead so the bracket spans the
 * whole selection.
 */
export function zonesToPaceKey(zones) {
  if (!zones || !zones.length) return null;
  const highest = Math.max(...zones.map((z) => parseInt(z, 10)).filter((n) => !isNaN(n)));
  return zoneNumberToPaceKey(highest);
}

/**
 * Decide whether a segment qualifies as "short" — i.e., short enough that
 * the athlete should be running at REPETITION pace (Daniels' R-pace) rather
 * than interval pace. Threshold: ≤ 90 s of work or ≤ 400 m.
 */
export function isShortRepSegment(seg) {
  if (!seg) return false;
  const v = parseFloat(seg.value);
  if (isNaN(v) || v <= 0) return false;
  const unit = (seg.unit || "").toLowerCase();
  if (unit === "sec" && v <= 90) return true;
  if (unit === "min" && v <= 1.5) return true;
  if (unit === "m" && v <= 400) return true;
  if ((unit === "km" || unit === "kilometers" || unit === "kilometres") && v <= 0.4) return true;
  if (unit === "miles" && v <= 0.25) return true;
  return false;
}

/**
 * Pick the right pace key given a zones array AND (optionally) the segment's
 * own value+unit. For Z5+ segments at very short durations/distances we step
 * down from "interval" pace to "repetition" pace (Daniels R).
 */
export function paceKeyForSegment(seg) {
  const baseKey = zonesToPaceKey(seg?.zones);
  if (baseKey === "interval" && isShortRepSegment(seg)) return "repetition";
  return baseKey;
}

/**
 * Given zones + athlete paces, produce a pace range label like "5:40–5:57/km".
 * Returns "" if no athlete paces.
 *
 * Accepts either an array of zones (legacy) or a full segment object so we can
 * apply short-rep logic.
 *
 * When multiple zones are selected (e.g. Z1+Z2, Z3+Z4, Z4+Z5) the bracket
 * SPANS the whole selection so the athlete sees the full allowed window:
 *   Z1+Z2 → "6:02+/km"     (top of Z2 and slower — open-ended slow side
 *                           because Z1's lactate value is open-ended slow)
 *   Z4+Z5 → "<5:20/km"     (bottom of Z4 and faster — open-ended fast side
 *                           because Z5's lactate value is open-ended fast)
 *   Z3+Z4 → "4:59–6:01/km" (fastest of Z4 to slowest of Z3 — closed range)
 */
export function suggestedPaceFromZones(zonesOrSegment, athletePaces) {
  if (!athletePaces) return "";
  const seg = Array.isArray(zonesOrSegment) ? { zones: zonesOrSegment } : zonesOrSegment;
  const buckets = collectZoneBuckets(seg, athletePaces, "pace");
  if (buckets.length === 0) return "";
  const agg = aggregateRanges(buckets, "pace");
  if (!agg) return "";
  if (agg.bound === "lower") return `${secToMinSec(agg.fast_sec_per_km)}+/km`;
  if (agg.bound === "upper") return `<${secToMinSec(agg.slow_sec_per_km)}/km`;
  return `${secToMinSec(agg.fast_sec_per_km)}–${secToMinSec(agg.slow_sec_per_km)}/km`;
}

/**
 * Given zones + athlete paces, produce an HR bracket label like
 * "160–167 bpm" / "172+ bpm" / "<144 bpm" — only when the underlying
 * lactate test recorded HR data. Returns "" for VDOT-only profiles.
 *
 * Multi-zone selections aggregate the same way as paces:
 *   Z1+Z2 → "145+ bpm"    (open-ended high)
 *   Z4+Z5 → "<167 bpm"    (open-ended low)  -- note: HR opposite of pace,
 *                                              fast pace ≈ high HR, but
 *                                              we honor whichever side is
 *                                              open in the source data.
 *   Z3+Z4 → "153–167 bpm"
 */
export function suggestedHrFromZones(zonesOrSegment, athletePaces) {
  if (!athletePaces) return "";
  const seg = Array.isArray(zonesOrSegment) ? { zones: zonesOrSegment } : zonesOrSegment;
  const buckets = collectZoneBuckets(seg, athletePaces, "hr");
  if (buckets.length === 0) return "";
  const agg = aggregateRanges(buckets, "hr");
  if (!agg) return "";
  if (agg.bound === "lower") return `${agg.low_bpm}+ bpm`;
  if (agg.bound === "upper") return `<${agg.high_bpm} bpm`;
  return `${agg.low_bpm}–${agg.high_bpm} bpm`;
}

// --- internal helpers for multi-zone aggregation -------------------------

/**
 * Resolve each selected zone number to its own pace bucket. Honors the
 * short-rep escalation (interval → repetition) when only a single Z5
 * segment is selected — the optimization doesn't make sense for mixed
 * Z4+Z5 selections, so we skip it there.
 */
function collectZoneBuckets(seg, athletePaces, kind) {
  const nums = (seg?.zones || [])
    .map((z) => parseInt(z, 10))
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return [];
  const onlyZ5 = nums.length === 1 && nums[0] >= 5;
  const out = [];
  for (const n of nums) {
    let key = zoneNumberToPaceKey(n);
    if (key === "interval" && onlyZ5 && isShortRepSegment(seg)) key = "repetition";
    if (!key) continue;
    let bucket = athletePaces[key];
    if (!bucket && key === "recovery") bucket = athletePaces.easy;
    if (!bucket) continue;
    if (kind === "hr") {
      if (bucket.hr) out.push(bucket.hr);
    } else {
      out.push(bucket);
    }
  }
  // De-dupe (Z1+Z2 on a VDOT-only profile both fall back to "easy").
  const seen = new Set();
  return out.filter((b) => {
    const k = JSON.stringify(b);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Reduce a list of bucket ranges to a single aggregated range that spans
 * the entire selection. Bound flags propagate so an open-ended source
 * zone keeps the combined range open on that side.
 */
function aggregateRanges(buckets, kind) {
  if (buckets.length === 0) return null;
  const isPace = kind === "pace";
  const fastKey = isPace ? "fast_sec_per_km" : "low_bpm";
  const slowKey = isPace ? "slow_sec_per_km" : "high_bpm";
  let fastest = Infinity;
  let slowest = -Infinity;
  let hasLower = false; // any bucket open on the slow side (Z1-style "X+")
  let hasUpper = false; // any bucket open on the fast side (Z5-style "<X")
  for (const b of buckets) {
    const f = b[fastKey];
    const s = b[slowKey];
    if (Number.isFinite(f)) fastest = Math.min(fastest, f);
    if (Number.isFinite(s)) slowest = Math.max(slowest, s);
    if (b.bound === "lower") hasLower = true;
    if (b.bound === "upper") hasUpper = true;
  }
  if (!Number.isFinite(fastest) || !Number.isFinite(slowest)) return null;
  // Note: pace and HR have OPPOSITE numeric directions vs runner intuition
  //   pace: fastest = SMALLER sec/km, slowest = LARGER sec/km
  //   HR:   low_bpm = lower number, high_bpm = higher number
  // The aggregation math is identical — we just keep the relevant value
  // for whichever side is left "open".
  let bound = null;
  if (hasLower && !hasUpper) bound = "lower";
  else if (hasUpper && !hasLower) bound = "upper";
  // If BOTH bounds are open (e.g. Z1+Z5 — odd selection), fall back to a
  // closed range using whatever numeric values we have.
  const out = { bound };
  out[fastKey] = fastest;
  out[slowKey] = slowest;
  return out;
}

// ---- Lactate-derived training paces ----

// Parse "5:30" → 330 sec; returns null if unparseable.
function parsePaceMinSec(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.trim().match(/^(\d+):(\d{1,2})/);
  if (!m) return null;
  const min = parseInt(m[1], 10);
  const sec = parseInt(m[2], 10);
  if (isNaN(min) || isNaN(sec)) return null;
  return min * 60 + sec;
}

// Parse a free-form pace range like "5:30 - 6:00" / "5:30-6:00" / "5:30 to 6:00"
// into { slow_sec_per_km, fast_sec_per_km, bound } where slow is the slower
// pace (larger seconds-per-km), fast is the faster one, and `bound` flags
// open-ended ranges so the display can use the proper convention:
//   bound === "lower"  → "6:27+"     (slower-open, no faster cap)
//   bound === "upper"  → "< 4:58"    (faster-open, no slower cap)
//   bound === null     → "4:59–5:20" (closed range)
function parsePaceRange(text) {
  if (!text) return null;
  const cleaned = String(text).replace(/[–—]/g, "-").trim();
  // "< 4:58" — faster-open ceiling (no slower bound)
  const lt = cleaned.match(/^\s*<\s*(\d+:\d{1,2})\s*$/);
  if (lt) {
    const v = parsePaceMinSec(lt[1]);
    return v != null ? { slow_sec_per_km: v, fast_sec_per_km: v, bound: "upper" } : null;
  }
  // "6:27 +" / "6:27+" — slower-open floor (no faster bound)
  const ge = cleaned.match(/^\s*(\d+:\d{1,2})\s*\+\s*$/);
  if (ge) {
    const v = parsePaceMinSec(ge[1]);
    return v != null ? { slow_sec_per_km: v, fast_sec_per_km: v, bound: "lower" } : null;
  }
  const parts = cleaned.split(/\s*(?:-|to)\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const a = parsePaceMinSec(parts[0]);
  const b = parts.length > 1 ? parsePaceMinSec(parts[1]) : a;
  if (a == null || b == null) return null;
  // Bigger sec/km = slower
  const slow = Math.max(a, b);
  const fast = Math.min(a, b);
  return { slow_sec_per_km: slow, fast_sec_per_km: fast, bound: null };
}

// Parse an HR range string like "160 - 167" / "172+" / "< 144" / "172+ bpm".
// Returns { low_bpm, high_bpm, bound } where bound mirrors the pace parser:
//   "lower" → open-ended slow side (e.g. "172+")
//   "upper" → open-ended fast side (e.g. "< 144")
//   null    → closed range
function parseHrRange(text) {
  if (!text) return null;
  // Strip a trailing "bpm" unit if the coach saved it that way.
  const cleaned = String(text).replace(/[–—]/g, "-").replace(/bpm/gi, "").trim();
  // "< 144" — faster-open ceiling (no slow bound — Z1-ish)
  const lt = cleaned.match(/^\s*<\s*(\d{2,3})\s*$/);
  if (lt) {
    const v = parseInt(lt[1], 10);
    return Number.isFinite(v) ? { low_bpm: v, high_bpm: v, bound: "upper" } : null;
  }
  // "172+" — slower-open floor (no high bound — Z5-ish)
  const ge = cleaned.match(/^\s*(\d{2,3})\s*\+\s*$/);
  if (ge) {
    const v = parseInt(ge[1], 10);
    return Number.isFinite(v) ? { low_bpm: v, high_bpm: v, bound: "lower" } : null;
  }
  const parts = cleaned.split(/\s*(?:-|to)\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const a = parseInt(parts[0], 10);
  const b = parts.length > 1 ? parseInt(parts[1], 10) : a;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return { low_bpm: Math.min(a, b), high_bpm: Math.max(a, b), bound: null };
}

// Match a lactate-zone label to one of our pace keys. Lactate test labels in
// TIR vary ("Z4", "Zone 4", "LT2", "Threshold", etc.) so we extract a numeric
// Z label first (covers both "Z4" and "Zone 4" — note: NOT just `\bz4\b`, the
// stored format is "Zone 4" with a space). Numeric labels MUST take strict
// priority over name words because zone *names* commonly contain words like
// "Tempo" that appear in multiple zones (Z3 "Endurance/Tempo" AND Z4 "Threshold")
// — that name collision was the root of the long-standing off-by-one bug.
function paceKeyForLactateZone(zone) {
  const label = (zone?.zone_label || "").toLowerCase();
  const name = (zone?.name || "").toLowerCase();
  // Try numeric extraction on the LABEL only (not name, which is noise).
  // Accepts: "Z4", "Zone 4", "Zone-4", "z4", "Z 4", "Zone4".
  const numMatch = label.match(/(?:^|\b)z(?:one)?[\s\-_]*(\d)\b/);
  if (numMatch) {
    const n = parseInt(numMatch[1], 10);
    if (n >= 5) return "interval";
    if (n === 4) return "threshold";
    if (n === 3) return "marathon";
    if (n === 2) return "easy";
    if (n === 1) return "recovery";
  }
  // Fallback to name-based matching only when no numeric label is present
  // (very rare — coaches who type free-form lactate zone labels). Order
  // matters: most-specific words first.
  const blob = `${label} ${name}`;
  if (/\b(anaerobic|vo2 ?max|repetition)\b/.test(blob)) return "interval";
  if (/\b(lt2|lactate threshold|threshold|tempo)\b/.test(blob)) return "threshold";
  if (/\b(marathon|aerobic[- ]?2|lt1|aet|endurance)\b/.test(blob)) return "marathon";
  if (/\b(easy|aerobic[- ]?1|fat ?max)\b/.test(blob)) return "easy";
  if (/\b(recovery)\b/.test(blob)) return "recovery";
  return null;
}

/**
 * Convert a lactate test (`{ zones: [{ zone_label, name, pace_range, ... }] }`)
 * into the same shape as `trainingPaces(vdot)`. Zones whose pace_range can't
 * be parsed are skipped. Returns null if no zones produced a usable pace.
 *
 * The resulting object can be slotted into the same `athletePaces` consumer
 * (`suggestedPaceFromZones`, `pacesForType`, etc.) so the rest of the planner
 * gets lactate-derived prescriptions for free when test data exists.
 */
export function lactateTrainingPaces(lactate) {
  if (!lactate || !Array.isArray(lactate.zones) || lactate.zones.length === 0) return null;
  const out = {};
  for (const z of lactate.zones) {
    const key = paceKeyForLactateZone(z);
    if (!key) continue;
    const range = parsePaceRange(z.pace_range);
    if (!range) continue;
    // Optional HR bracket from the same row — coaches can prescribe by
    // pace OR HR (especially on trails / hills where pace lies).
    const hr = parseHrRange(z.hr_range);
    if (hr) range.hr = hr;
    // If multiple lactate zones map to the same key (e.g. two "Z2"s), keep
    // the slower one — coaches typically want a conservative bracket.
    const existing = out[key];
    if (!existing || range.slow_sec_per_km > existing.slow_sec_per_km) {
      out[key] = range;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}
